import React from 'react'
import { useTranslation } from 'react-i18next'
import { RefreshCw, UserRound, Users, X } from 'lucide-react'
import { Avatar } from '@/components/ui/avatar'
import { Button } from '@/components/ui/button'
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog'
import { cn } from '@/lib/utils'
import { registerStaleSaveHost, type EditConflict, type StaleChoice } from '@/lib/realtime/editConflict'

interface EditConflictNoticesProps {
  conflict: EditConflict
  /** Drops the local edits and reads the item again. */
  onReload: () => unknown
  className?: string
}

/**
 * The two notices of an editor: who else has it open ("Fulano também está
 * editando") and a save from someone else ("Fulano salvou alterações —
 * Recarregar"). Local edits stay until the person reloads.
 */
export const EditConflictNotices = ({ conflict, onReload, className }: EditConflictNoticesProps) => {
  const { t } = useTranslation('common')
  const [reloading, setReloading] = React.useState(false)
  const { editors, saved } = conflict
  if (!editors.length && !saved) return null
  const first = editors[0]
  /* Community chat off: nobody is named or shown, only that someone of the team edits too. */
  const anonymous = Boolean(first?.anonymous)
  const nameOf = (name: string | null | undefined) => name || t('presence.someone')
  const reload = async () => {
    setReloading(true)
    try {
      await onReload()
      conflict.dismissSaved()
    } finally {
      setReloading(false)
    }
  }
  return (
    <div className={cn('space-y-2', className)} data-testid="edit-conflict-notices">
      {first && (
        <div className="flex items-center gap-3 rounded-xl border border-blue-200 bg-blue-50 px-4 py-2.5 text-sm text-blue-900" role="status" data-testid="edit-conflict-editing">
          {anonymous ? (
            <span className="flex h-7 w-7 flex-shrink-0 items-center justify-center rounded-full border-2 border-white bg-blue-100 text-blue-700" aria-hidden>
              <UserRound className="h-4 w-4" />
            </span>
          ) : (
            <div className="flex -space-x-2">
              {editors.slice(0, 3).map((person) => (
                <Avatar
                  key={person.memberId}
                  src={person.avatarUrl}
                  alt=""
                  fallback={person.name || '?'}
                  size="sm"
                  className={cn('h-7 w-7 border-2 border-white', person.state === 'idle' && 'opacity-40 grayscale')}
                />
              ))}
            </div>
          )}
          <span className="min-w-0">
            {anonymous
              ? t('presence.anotherEditing', { count: editors.length })
              : editors.length > 1
              ? t('presence.alsoEditingMany', { name: nameOf(first.name), count: editors.length - 1 })
              : t('presence.alsoEditing', { name: nameOf(first.name) })}
          </span>
        </div>
      )}
      {saved && (
        <div className="flex flex-wrap items-center gap-3 rounded-xl border border-amber-200 bg-amber-50 px-4 py-2.5 text-sm text-amber-900" role="alert" data-testid="edit-conflict-saved">
          <Users className="h-4 w-4 flex-shrink-0 text-amber-600" aria-hidden />
          <span className="min-w-0 flex-1">
            {saved.by?.name ? t('presence.savedBy', { name: saved.by.name }) : t('presence.savedBySomeone')}
          </span>
          <div className="flex items-center gap-1">
            <Button size="sm" variant="outline" className="gap-1.5 bg-white" disabled={reloading} onClick={() => void reload()} data-testid="edit-conflict-reload">
              <RefreshCw className={cn('h-3.5 w-3.5', reloading && 'animate-spin')} />
              {t('presence.reload')}
            </Button>
            <Button size="sm" variant="ghost" className="h-8 w-8 p-0 text-amber-700" aria-label={t('presence.dismiss')} onClick={conflict.dismissSaved}>
              <X className="h-4 w-4" />
            </Button>
          </div>
        </div>
      )}
    </div>
  )
}

/**
 * The one dialog of a stale save (409): the stored version (reload, my
 * changes are dropped) or mine (saved over it on purpose). Closing it keeps
 * editing without saving. Mounted once in the shell.
 */
export const StaleSaveDialogHost = () => {
  const { t } = useTranslation('common')
  const [pending, setPending] = React.useState<{ resolve: (choice: StaleChoice) => void } | null>(null)
  const queue = React.useRef<{ resolve: (choice: StaleChoice) => void }[]>([])
  const pendingRef = React.useRef<{ resolve: (choice: StaleChoice) => void } | null>(null)

  React.useEffect(() => {
    registerStaleSaveHost((request) => {
      /* Two saves can go stale at once: one dialog at a time. */
      if (pendingRef.current) {
        queue.current.push(request)
        return
      }
      pendingRef.current = request
      setPending(request)
    })
    return () => registerStaleSaveHost(null)
  }, [])

  const answer = (choice: StaleChoice) => {
    pendingRef.current?.resolve(choice)
    pendingRef.current = queue.current.shift() || null
    setPending(pendingRef.current)
  }

  return (
    <Dialog open={Boolean(pending)} onOpenChange={(open) => { if (!open) answer('cancel') }}>
      <DialogContent className="w-[calc(100%-1.5rem)] max-w-md p-4 sm:p-6" data-testid="stale-save-dialog">
        <DialogHeader>
          <DialogTitle>{t('presence.staleTitle')}</DialogTitle>
          <DialogDescription>{t('presence.staleDescription')}</DialogDescription>
        </DialogHeader>
        <ul className="space-y-2 text-sm text-gray-600">
          <li><span className="font-medium text-gray-900">{t('presence.staleReload')}:</span> {t('presence.staleReloadHint')}</li>
          <li><span className="font-medium text-gray-900">{t('presence.staleOverwrite')}:</span> {t('presence.staleOverwriteHint')}</li>
        </ul>
        <DialogFooter className="gap-2">
          <Button variant="outline" className="w-full sm:w-auto" onClick={() => answer('overwrite')} data-testid="stale-save-overwrite">
            {t('presence.staleOverwrite')}
          </Button>
          <Button variant="secondary" className="w-full sm:w-auto" onClick={() => answer('reload')} data-testid="stale-save-reload">
            {t('presence.staleReload')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
